import React from 'react';

interface AppSplashProps {
    /** Optional status line, e.g. "Checking your session". Keep it short. */
    message?: string;
    /** Tenant logo URL, when system configuration has already resolved. */
    logoUrl?: string | null;
    /** Tenant name, used for the fallback monogram and the alt text. */
    name?: string | null;
}

/**
 * Full-screen loading state used while auth/tenant/system config resolve.
 *
 * Replaces the old bare "Loading..." text, which rendered as black text on an
 * unthemed white page even when the user was in dark mode.
 */
export const AppSplash: React.FC<AppSplashProps> = ({ message, logoUrl, name }) => {
    const initial = (name || 'Q').charAt(0).toUpperCase();

    return (
        <div
            className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6"
            role="status"
            aria-live="polite"
        >
            <div className="relative flex items-center justify-center">
                {/* Soft brand halo so the mark does not sit on a flat void. */}
                <span
                    aria-hidden="true"
                    className="absolute h-24 w-24 rounded-full bg-primary/15 blur-2xl"
                />
                {logoUrl ? (
                    <img
                        src={logoUrl}
                        alt={name || 'Logo'}
                        className="relative h-14 w-auto max-w-[180px] object-contain"
                    />
                ) : (
                    <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-2xl font-bold text-primary-foreground">
                        {initial}
                    </span>
                )}
            </div>

            <div className="flex flex-col items-center gap-3">
                <span
                    aria-hidden="true"
                    className="h-5 w-5 animate-spin rounded-full border-2 border-primary/25 border-t-primary"
                />
                <span className="text-sm text-muted-foreground">{message || 'Loading…'}</span>
            </div>
        </div>
    );
};

export default AppSplash;
